import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { ArrowUp, ArrowDown } from "lucide-react";


/**
 * BranchSalesTicker
 * ------------------
 * A trading-terminal style dashboard for branch sales data.
 *
 * - Polls `apiUrl` every `pollInterval` ms (default 2 minutes).
 * - Every poll is appended to a same-day history array and persisted
 *   to localStorage (key: STORAGE_KEY), so a page refresh doesn't
 *   lose the day's ticks.
 * - History automatically resets when the calendar day changes.
 * - Renders a ticker strip (like a stock exchange tape), branch
 *   "price cards" with up/down deltas, and a line chart of NetAmount
 *   over time for the selected branch (or all branches combined).
 *
 * The endpoint is date-scoped, e.g.:
 *   https://cubixweberp.com:213/api/HRDashboard/shinka/-/Todays_Sales_Branch/2026-07-03/2026-07-03/-/-/-/-/-
 * so by default this component rebuilds the URL on every poll using
 * today's date for the {from}/{to} segments (see buildTodayUrl below).
 * That means it keeps working correctly across a midnight rollover
 * without needing a page refresh.
 *
 * Usage:
 *   <BranchSalesTicker />                          // uses today's date automatically
 *   <BranchSalesTicker apiBaseUrl="https://..." />  // point at a different host, still date-scoped
 *   <BranchSalesTicker apiUrl="https://exact-url" /> // bypass date-building entirely
 *
 * Expected API response shape (array):
 *   [{ Branch_ID, Branch_Name, CashSale, CreditSale, CashReturn, CreditReturn, NetAmount }, ...]
 */

const STORAGE_KEY = "branchSalesHistory_v1";
const DEFAULT_POLL_MS = 2 * 60 * 1000; // 2 minutes
const MAX_POINTS = 500; // safety cap so localStorage never grows unbounded

const COLORS = {
  bg: "#FFFFFF",
  panel: "#F7F8FA",
  border: "#E6E8EB",
  text: "#1E2329",
  subtext: "#707A8A",
  green: "#0ECB81",
  red: "#F6465D",
  blue: "#2563EB",
  amber: "#B7791F",
};

const LINE_PALETTE = [
  COLORS.blue,
  COLORS.amber,
  "#7C3AED",
  "#0891B2",
  "#DB2777",
];

function todayKey() {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { day: todayKey(), ticks: [] };
    const parsed = JSON.parse(raw);
    if (parsed.day !== todayKey()) return { day: todayKey(), ticks: [] };
    return parsed;
  } catch {
    return { day: todayKey(), ticks: [] };
  }
}

function saveHistory(history) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (err) {
    console.error("Failed to persist sales history to localStorage:", err);
  }
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function fmtTimeShort(ts) {
  // Compact time label for narrow screens, e.g. "10:30a" instead of "10:30 AM"
  const full = new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  return full.replace(/\s?AM$/i, "a").replace(/\s?PM$/i, "p");
}

function useIsNarrowScreen(breakpoint = 480) {
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isNarrow;
}

export default function DailySalesTicker({
  pollInterval = DEFAULT_POLL_MS,
  fullUrl,
}) {
  const [history, setHistory] = useState(() => loadHistory());
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState("ALL");
  const [rangeMinutes, setRangeMinutes] = useState(240); // default: 4H window
  const [lastUpdated, setLastUpdated] = useState(null);
  const isNarrow = useIsNarrowScreen();
  const intervalRef = useRef(null);

  const fetchAndStore = useCallback(async () => {
    try {
      setError(null);
      const url = fullUrl;
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`API responded with ${res.status}`);
      const data = await res.json();

      setHistory((prev) => {
        const day = todayKey();
        const base = prev.day === day ? prev.ticks : [];
        const nextTicks = [...base, { ts: Date.now(), rows: data }].slice(
          -MAX_POINTS
        );
        const next = { day, ticks: nextTicks };
        saveHistory(next);
        return next;
      });
      setLastUpdated(Date.now());
    } catch (err) {
      setError(err.message || "Failed to fetch sales data");
    } finally {
      setLoading(false);
    }
  }, [fullUrl]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.getPropertyValue("overflow-x");
    const prevBody = body.style.getPropertyValue("overflow-x");
    html.style.setProperty("overflow-x", "hidden", "important");
    body.style.setProperty("overflow-x", "hidden", "important");
    return () => {
      html.style.setProperty("overflow-x", prevHtml || "");
      body.style.setProperty("overflow-x", prevBody || "");
    };
  }, []);

  useEffect(() => {
    fetchAndStore();
    intervalRef.current = setInterval(fetchAndStore, pollInterval);
    return () => clearInterval(intervalRef.current);
  }, [fetchAndStore, pollInterval]);

  const ticks = history.ticks;
  const latest = ticks.length ? ticks[ticks.length - 1].rows : [];
  const previous = ticks.length > 1 ? ticks[ticks.length - 2].rows : [];

  const branches = latest.length ? latest : previous;

  const getPrev = (branchId) => previous.find((b) => b.Branch_ID === branchId);

  // Build chart data: one point per tick, one field per branch (NetAmount)
  const chartData = ticks.map((t) => {
    const point = { time: fmtTime(t.ts), timeShort: fmtTimeShort(t.ts) };
    t.rows.forEach((r) => {
      point[r.Branch_ID] = r.NetAmount;
    });
    point.__TOTAL__ = t.rows.reduce((sum, r) => sum + (r.NetAmount || 0), 0);
    return point;
  });

  const branchIds = branches.map((b) => b.Branch_ID);
  const linesToRender = selectedBranch === "ALL" ? branchIds : [selectedBranch];

  const pointsPerWindow =
    rangeMinutes === "ALL"
      ? chartData.length
      : Math.ceil(rangeMinutes / (pollInterval / 60000));
  const visibleData =
    rangeMinutes === "ALL" ? chartData : chartData.slice(-pointsPerWindow);

  // Space out x-axis labels so they never overlap, regardless of point count.
  // Narrow screens get fewer visible labels since rotated text still needs room.
  const targetLabelCount = isNarrow ? 5 : 8;
  const xAxisInterval = Math.max(
    0,
    Math.ceil(visibleData.length / targetLabelCount) - 1
  );

  const totalNet = branches.reduce((s, b) => s + (b.NetAmount || 0), 0);
  const totalPrevNet = previous.reduce((s, b) => s + (b.NetAmount || 0), 0);
  const totalDelta = totalNet - totalPrevNet;

  return (
    <div className="daily-sales-ticker" style={styles.app}>
      <style>{tickerAnimationCSS}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerTitle}>BRANCH SALES · LIVE</div>
          <div style={styles.headerSub}>
            {lastUpdated
              ? `Last updated ${fmtTime(lastUpdated)}`
              : "Connecting…"}
          </div>
        </div>
        <div style={styles.totalBox}>
          <div style={styles.headerSub}>TOTAL NET</div>
          <div
            style={{
              ...styles.totalValue,
              color: "#000000",
            }}
          >
            {fmtMoney(totalNet)}
            {/* <span style={styles.deltaTag}>
              {totalDelta >= 0 ? "▲" : "▼"} {fmtMoney(Math.abs(totalDelta))}
            </span> */}
          </div>
        </div>
      </div>

      {error && <div style={styles.errorBanner}>⚠ {error}</div>}

      {/* Ticker tape */}
      <div style={styles.tickerTrack}>
        <div style={styles.tickerTapeInner}>
          {[...branches, ...branches].map((b, i) => {
            const prev = getPrev(b.Branch_ID);
            const delta = prev ? b.NetAmount - prev.NetAmount : 0;
            const up = delta >= 0;
            return (
              <span key={i} style={styles.tickerItem}>
                <b style={{ color: COLORS.text }}>{b.Branch_Name}</b>{" "}
                {/* <span style={{ color: up ? COLORS.green : COLORS.red }}> */}
                <span style={{ color: "#717171", marginLeft:"14px" }}>Net Amount</span>
                <span
                  style={{
                    color: "#000000",
                    fontSize: "14px",
                    marginLeft: "5px",
                    fontWeight: "bold",
                  }}
                >
                  {/* {fmtMoney(b.NetAmount)} {up ? "▲" : "▼"}{" "} */}
                  {fmtMoney(b.NetAmount)} 
                </span>
                {/* ADDED BREAKDOWN SEGMENT */}
                <div>
                  <span
                    style={{
                      color: COLORS.subtext,
                      fontSize: 11,
                      flexDirection:"row",
                      display:"inline-flex",
                      alignItems:"center"

                    }}
                  >
                     <ArrowUp size={18} strokeWidth={3}  style={{
                        color: "#40C057",
                        fontSize: "14px",
                        marginLeft: "2px",
                        fontWeight: "bold",
                      }} />  
                    Cash :{" "}
                    <span
                      style={{
                        color: "#40C057",
                        fontSize: "14px",
                        marginLeft: "2px",
                        fontWeight: "bold",
                        marginRight:"8px"
                      }}
                    >
                     {fmtMoney(b.CashSale)}
                    </span>{" "}
                    |  <ArrowUp size={18} strokeWidth={3}   style={{
                        color: "#40C057",
                        fontSize: "14px",
                        marginLeft: "2px",
                        fontWeight: "bold",
                      }} />  Credit:{" "}
                    <span
                      style={{
                        color: "#40C057",
                        fontSize: "14px",
                        marginLeft: "2px",
                        fontWeight: "bold",
                        marginRight:"8px"
                      }}
                    >
                      {fmtMoney(b.CreditSale)}{" "}
                    </span>
                  </span>
                </div>

                <div>
                  <span
                    style={{
                      color: COLORS.subtext,
                      fontSize: 11,
                        flexDirection:"row",
                      display:"inline-flex",
                      alignItems:"center"

                    }}
                  >
                    <ArrowDown size={18} strokeWidth={3}  style={{
                        color: "#f00",
                        fontSize: "14px",
                        marginLeft: "2px",
                        fontWeight: "bold",
                      }} /> 
                    Cash :{" "}
                    <span
                      style={{
                        color: "#f00",
                        fontSize: "14px",
                        marginLeft: "2px",
                        fontWeight: "bold",
                        marginRight:"8px"
                      }}
                    >
                     {Math.abs( fmtMoney(b.CashReturn) ) }  {" "}
                    </span>{" "}
                      | <ArrowDown size={18} strokeWidth={3}  style={{
                        color: "#f00",
                        fontSize: "14px",
                        marginLeft: "2px",
                        fontWeight: "bold",
                      }}/> Credit :{" "}
                    <span
                      style={{
                        color: "#f00",
                        fontSize: "14px",
                        marginLeft: "2px",
                        fontWeight: "bold",
                      }}
                    >
                        {Math.abs( fmtMoney(b.CreditReturn) ) } 
                    </span>
                  </span>
                </div>
              </span>

            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div style={styles.chartPanel}>
        <div style={styles.chartHeader}>
          <span style={styles.chartTitle}>
            NET AMOUNT TREND —{" "}
            {selectedBranch === "ALL" ? "ALL BRANCHES" : selectedBranch}
          </span>
          <div style={styles.legendRow}>
            {[
              { label: "1H", value: 60 },
              { label: "4H", value: 240 },
              { label: "TODAY", value: "ALL" },
            ].map((r) => (
              <button
                key={r.label}
                onClick={() => setRangeMinutes(r.value)}
                style={{
                  ...styles.legendPill,
                  borderColor:
                    rangeMinutes === r.value ? COLORS.amber : COLORS.border,
                  fontWeight: rangeMinutes === r.value ? 700 : 400,
                }}
              >
                {r.label}
              </button>
            ))}
            <span style={styles.legendDivider} />
            <button
              onClick={() => setSelectedBranch("ALL")}
              style={{
                ...styles.legendPill,
                borderColor:
                  selectedBranch === "ALL" ? COLORS.amber : COLORS.border,
              }}
            >
              ALL
            </button>
            {branchIds.map((id, i) => (
              <button
                key={id}
                onClick={() => setSelectedBranch(id)}
                style={{
                  ...styles.legendPill,
                  color: LINE_PALETTE[i % LINE_PALETTE.length],
                  borderColor:
                    selectedBranch === id ? COLORS.amber : COLORS.border,
                }}
              >
                {id}
              </button>
            ))}
          </div>
        </div>

        <div style={{ width: "100%", height: isNarrow ? 300 : 340 }}>
          {visibleData.length === 0 ? (
            <div style={styles.emptyState}>
              {loading ? "Loading first tick…" : "No ticks recorded yet today"}
            </div>
          ) : (
            <ResponsiveContainer>
              <LineChart
                data={visibleData}
                margin={{
                  top: 10,
                  right: 20,
                  left: 0,
                  bottom: isNarrow ? 24 : 0,
                }}
              >
                <CartesianGrid
                  stroke={COLORS.border}
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  dataKey={isNarrow ? "timeShort" : "time"}
                  stroke={COLORS.subtext}
                  tick={{ fontSize: isNarrow ? 10 : 11 }}
                  interval={xAxisInterval}
                  minTickGap={isNarrow ? 8 : 20}
                  angle={isNarrow ? -45 : 0}
                  textAnchor={isNarrow ? "end" : "middle"}
                  height={isNarrow ? 45 : 30}
                />
                <YAxis
                  stroke={COLORS.subtext}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => v.toLocaleString()}
                  width={70}
                />
                <ReferenceLine y={0} stroke={COLORS.border} />
                <Tooltip
                  contentStyle={{
                    background: COLORS.panel,
                    border: `1px solid ${COLORS.border}`,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: COLORS.subtext }}
                  formatter={(value, name) => [fmtMoney(value), name]}
                />
                {selectedBranch === "ALL" &&
                  branchIds.map((id, i) => (
                    <Line
                      key={id}
                      type="monotone"
                      dataKey={id}
                      stroke={LINE_PALETTE[i % LINE_PALETTE.length]}
                      dot={false}
                      strokeWidth={2}
                      isAnimationActive={false}
                    />
                  ))}
                {selectedBranch !== "ALL" && (
                  <Line
                    type="monotone"
                    dataKey={selectedBranch}
                    stroke={COLORS.green}
                    dot={false}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

const tickerAnimationCSS = `
@keyframes ticker-scroll {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
.daily-sales-ticker, .daily-sales-ticker *, .daily-sales-ticker *::before, .daily-sales-ticker *::after {
  box-sizing: border-box;
  min-width: 0;
}
.daily-sales-ticker {
  max-width: 100%;
  overflow-wrap: anywhere;
}

.card-header-block {
    display: flex;
    flex-direction: column !important;
    align-items: flex-start !important;
    width: 100%;
    gap: 4px;
  }
  
  .branch-title-container {
    display: flex;
    flex-direction: row;
    /* CHANGE THIS from center to flex-start */
    align-items: flex-start; 
    width: 100%;
  }
  
  .branch-text {
    word-break: break-word;
    line-height: 1.2; /* Ensures consistent height behavior when text wraps */
  }
  
  .card-net-amount {
    display: block;
    /* If you want the amount to align directly under the text instead of under the dot, uncomment this: */
    /* margin-left: 14px; */
  }
  .cards-container {
    display: flex;
    flex-direction: row; /* or flex-wrap: wrap */
    
    /* FIX: Forces all cards to line up perfectly at the top edge */
    align-items: flex-start !important; 
  }

@media (max-width: 480px) {
  .daily-sales-ticker {
    padding: 14px !important;
  }
  /* MOBILITY OVERRIDE: Forces the grid to become an inline horizontal scroller */
  .branch-cards-grid {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px !important; /* Leaves breathing space for mobile scrollbar */
    width: 100%;
  }
  
  .card-header-block {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 2px !important;
  }
  
  .card-net-amount {
    margin-left: 0 !important; /* Forces the drop to a new line on mobile layout */
    font-size: 14px !important; 
  }
}
`;

const styles = {
  app: {
    background: COLORS.bg,
    color: COLORS.text,
    padding: "20px",
    borderRadius: 12,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: "10px",
    marginBottom: "50px",
    border: `1px solid ${COLORS.border}`,
    boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    flexWrap: "wrap",
    rowGap: 8,
    marginBottom: 16,
  },
  headerLeft: {
    minWidth: 0,
  },
  headerTitle: {
    fontSize: 13,
    letterSpacing: 2,
    color: COLORS.subtext,
    fontWeight: 700,
  },
  headerSub: {
    fontSize: 11,
    color: COLORS.subtext,
    marginTop: 4,
  },
  totalBox: { textAlign: "right", minWidth: 0, flex: "0 1 auto" },
  totalValue: {
    fontSize: "clamp(16px, 5vw, 20px)",
    fontWeight: 700,
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  deltaTag: {
    fontSize: 13,
    fontWeight: 500,
  },
  errorBanner: {
    background: "rgba(246,70,93,0.12)",
    border: `1px solid ${COLORS.red}`,
    color: COLORS.red,
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 12,
    marginBottom: 12,
  },
  tickerTrack: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    background: COLORS.panel,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: "8px 0",
    marginBottom: 16,
  },
  tickerTapeInner: {
    display: "inline-block",
    animation: "ticker-scroll 50s linear infinite",
  },
  tickerItem: {
    display: "inline-block",
    fontSize: 12,
    padding: "0 24px",
    borderRight: `1px solid ${COLORS.border}`,
  },
  cardGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    marginBottom: 16,
    alignItems: "stretch", // Ensures cards in the same row still maintain equal height!
  },
  card: {
    background: COLORS.bg,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: "12px 14px",
    textAlign: "left",
    cursor: "pointer",
    color: COLORS.text,
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: COLORS.subtext,
    marginBottom: 6,
  },
  cardName: { fontWeight: 600, color: COLORS.text, fontSize: 11 },
  cardId: { fontSize: 10 },
  cardNet: {
    fontSize: 20,
    fontWeight: 700,
  },
  cardDelta: {
    fontSize: 12,
    marginTop: 2,
  },
  cardBreakdown: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 10,
    color: COLORS.subtext,
    marginTop: 8,
  },
  chartPanel: {
    background: COLORS.panel,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: 16,
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 10,
  },
  chartTitle: {
    fontSize: 12,
    letterSpacing: 1,
    color: COLORS.subtext,
    fontWeight: 600,
    minWidth: 0,
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  legendRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
  },
  legendDivider: {
    width: 1,
    height: 16,
    background: COLORS.border,
    margin: "0 4px",
  },
  legendPill: {
    background: "transparent",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 20,
    padding: "3px 10px",
    fontSize: 11,
    cursor: "pointer",
  },
  emptyState: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: COLORS.subtext,
    fontSize: 13,
  },
  footerNote: {
    marginTop: 10,
    fontSize: 11,
    color: COLORS.subtext,
    textAlign: "center",
  },
  //   cardHeaderBlock: {
  //     display: "flex",
  //     alignItems: "center",
  //     justifyContent: "space-between",
  //     width: "100%",
  //   },
};
